import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "..\/..\/interfaces\/DecisionPriority";
import { GameState, Position, PieceType, IMove, IPiece } from "..\/..\/..\/..\/types";
import { MCTSUtils_v2 } from "..\/..\/..\/utils\/MCTSUtils";

/**
 * Priority 17: Strategic Advance
 *
 * Encourages moving pieces to defensive squares that support other pieces,
 * creating strong defensive formations while advancing toward the opponent.
 *
 * Key concepts:
 * - Pieces should advance to squares where they can defend other pieces
 * - Prioritize forming defensive chains and formations
 * - Advance pieces that can protect valuable pieces (especially the king)
 * - Avoid leaving pieces isolated without mutual support
 */
export class StrategicAdvancePriority extends BaseDecisionPriority {
  readonly priority = 26;
  readonly name = "Strategic Advance";
  readonly description =
    "Provide strategic advance priors for MCTS - no longer filters moves";

  private analysisLogs: string[] = [];
  private strategicScores: Map<string, number> = new Map(); // Move ID -> Strategic Score

  isRelevant(context: DecisionContext): boolean {
    // Strategic advance scoring is always relevant for MCTS guidance
    const gameState = context.gameState;

    // Skip if immediate tactical priorities are likely active
    if (this.isInTacticalMode(gameState)) {
      return false;
    }

    // Always relevant for mid-game positional play
    return true;
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];
    this.strategicScores.clear();

    const gameState = context.gameState;
    const allMoves = context.allPossibleMoves;

    // Score all moves for strategic advance value
    const scoredMoves = this.scoreMovesForStrategicAdvance(gameState, allMoves);

    // Convert scores to normalized priors (0.0 to 1.0)
    const priorAnalysis = this.convertScoresToPriors(scoredMoves);

    // Log strategic analysis for debugging
    this.addLog(`📊 STRATEGIC ADVANCE PRIORS (${scoredMoves.length} moves):`);
    scoredMoves.forEach((sm, index) => {
      const moveStr = this.formatMove(gameState, sm.move);
      const moveId = this.getMoveId(sm.move);
      const prior = this.strategicScores.get(moveId) || 0;
      this.addLog(
        `  ${index + 1}. ${moveStr} → Prior: ${prior.toFixed(
          3
        )} (Raw: ${sm.score.toFixed(1)})`
      );
    });

    this.addLog(`🎯 Strategic Analysis Summary:`);
    this.addLog(`  - Positive moves: ${priorAnalysis.positiveMovesCount}`);
    this.addLog(
      `  - Best strategic prior: ${priorAnalysis.bestPrior.toFixed(3)}`
    );
    this.addLog(`  - Average prior: ${priorAnalysis.averagePrior.toFixed(3)}`);

    // Pass ALL moves through with strategic priors for MCTS
    return this.createFilterResult(
      context.allPossibleMoves, // Pass all moves through
      "Strategic advance priors calculated for MCTS evaluation",
      {
        logs: this.analysisLogs,
        strategicScores: Object.fromEntries(this.strategicScores),
        positiveMovesCount: priorAnalysis.positiveMovesCount,
        bestPrior: priorAnalysis.bestPrior,
        averagePrior: priorAnalysis.averagePrior,
      }
    );
  }

  private scoreMovesForStrategicAdvance(
    gameState: GameState,
    moves: MoveInfo[]
  ): Array<{ move: MoveInfo; score: number }> {
    return moves.map((move) => ({
      move,
      score: this.calculateStrategicAdvanceScore(gameState, move),
    }));
  }

  private calculateStrategicAdvanceScore(
    gameState: GameState,
    move: MoveInfo
  ): number {
    let score = 0;
    const toPosition = move.move.position;
    const fromPosition = move.fromPosition;
    const movingPiece = this.getPieceAt(gameState, fromPosition);

    if (!movingPiece) return 0;

    // 1. DEFENSIVE SUPPORT BONUS: Can this piece defend others from new position?
    const defensiveSupport = this.calculateDefensiveSupportValue(
      gameState,
      toPosition,
      movingPiece
    );
    score += defensiveSupport * 12;

    // 2. ADVANCEMENT BONUS: Moving toward opponent (but not recklessly)
    const advancementValue = this.calculateAdvancementValue(
      fromPosition,
      toPosition,
      gameState.isLightTurn
    );
    score += advancementValue * 20;

    // 3. FORMATION STRENGTH: How well does this fit with our formation?
    const formationValue = this.calculateFormationValue(
      gameState,
      toPosition,
      movingPiece
    );
    score += formationValue * 8;

    // 4. KING PROXIMITY BONUS: Staying near our king for mutual protection
    const kingProximity = this.calculateKingProximityValue(
      gameState,
      toPosition,
      movingPiece
    );
    score += kingProximity * 6;

    // 5. ISOLATION PENALTY: Avoid moving to isolated squares
    const isolationPenalty = this.calculateIsolationPenalty(
      gameState,
      toPosition
    );
    score -= isolationPenalty * 15;

    // 6. RETREAT PENALTY: Heavily penalize moving backward
    if (advancementValue < 0) {
      score += advancementValue * 30;
    }

    return score;
  }

  private calculateDefensiveSupportValue(
    gameState: GameState,
    position: Position,
    piece: IPiece
  ): number {
    let supportValue = 0;
    const ourColor = gameState.isLightTurn;

    // Check adjacent squares for our pieces that this piece could defend
    const adjacentSquares = this.getAdjacentSquares(position);

    for (const adjPos of adjacentSquares) {
      const piecesAtSquare = this.getPiecesAt(gameState, adjPos);
      for (const adjacentPiece of piecesAtSquare) {
        if (adjacentPiece.isLight === ourColor && !adjacentPiece.isKing) {
          // Basic support value
          supportValue += 1;

          // Bonus for supporting valuable pieces
          if (this.isValuablePiece(adjacentPiece)) {
            supportValue += 2;
          }
        }
      }
    }

    return supportValue;
  }

  private calculateAdvancementValue(
    fromPosition: Position,
    toPosition: Position,
    isLight: boolean
  ): number {
    // Light advances toward row 7, Dark advances toward row 0
    const targetRow = isLight ? 7 : 0;

    const oldDistance = Math.abs(fromPosition.row - targetRow);
    const newDistance = Math.abs(toPosition.row - targetRow);

    // Positive score if advancing, negative if retreating
    return oldDistance - newDistance;
  }

  private calculateFormationValue(
    gameState: GameState,
    position: Position,
    piece: IPiece
  ): number {
    let formationValue = 0;
    const ourColor = gameState.isLightTurn;

    // Check for pieces in formation (diagonal and adjacent support)
    const nearbySquares = this.getNearbySquares(position, 2);

    let supportingPieces = 0;
    for (const nearPos of nearbySquares) {
      const pieces = this.getPiecesAt(gameState, nearPos);
      for (const nearPiece of pieces) {
        if (nearPiece.isLight === ourColor) {
          supportingPieces++;
        }
      }
    }

    // Bonus for being part of a formation
    formationValue = Math.min(supportingPieces, 4); // Cap at 4 for diminishing returns

    return formationValue;
  }

  private calculateKingProximityValue(
    gameState: GameState,
    position: Position,
    piece: IPiece
  ): number {
    if (piece.isKing) return 0; // Don't apply to king itself

    const ourKing = this.findOurKing(gameState);
    if (!ourKing) return 0;

    const distance = this.calculateDistance(position, ourKing.position);

    // Closer to king is better (but not too close)
    if (distance === 1) return 3; // Adjacent is great
    if (distance === 2) return 2; // Near is good
    if (distance === 3) return 1; // Reasonable
    return 0; // Too far or too close
  }

  private calculateIsolationPenalty(
    gameState: GameState,
    position: Position
  ): number {
    const ourColor = gameState.isLightTurn;
    const adjacentSquares = this.getAdjacentSquares(position);

    let friendlyNeighbors = 0;
    for (const adjPos of adjacentSquares) {
      const pieces = this.getPiecesAt(gameState, adjPos);
      if (pieces.some((p) => p.isLight === ourColor)) {
        friendlyNeighbors++;
      }
    }

    // High penalty for complete isolation
    return friendlyNeighbors === 0 ? 5 : 0;
  }

  private isInTacticalMode(gameState: GameState): boolean {
    // Simple heuristic: if kings are close to racing, this priority is less relevant
    const ourKing = this.findOurKing(gameState);
    const opponentKing = this.findOpponentKing(gameState);

    if (!ourKing || !opponentKing) return false;

    const ourDistanceToWin = gameState.isLightTurn
      ? 7 - ourKing.position.row
      : ourKing.position.row;
    const opponentDistanceToWin = !gameState.isLightTurn
      ? 7 - opponentKing.position.row
      : opponentKing.position.row;

    // If either king is 2 moves from winning, tactical priorities take over
    return ourDistanceToWin <= 2 || opponentDistanceToWin <= 2;
  }

  private isValuablePiece(piece: IPiece): boolean {
    // Define valuable pieces that deserve extra protection
    return [
      PieceType.Bear,
      PieceType.Rhino,
      PieceType.Wolf,
      PieceType.Tree,
    ].includes(piece.type);
  }

  // Utility methods
  private getPieceAt(gameState: GameState, position: Position): IPiece | null {
    if (!this.isValidPosition(position)) return null;
    const pieces = gameState.board[position.row][position.col].Pieces;
    return pieces.length > 0 ? pieces[0] : null;
  }

  private getPiecesAt(gameState: GameState, position: Position): IPiece[] {
    if (!this.isValidPosition(position)) return [];
    return gameState.board[position.row][position.col].Pieces || [];
  }

  private getAdjacentSquares(position: Position): Position[] {
    const adjacent = [
      { row: position.row - 1, col: position.col },
      { row: position.row + 1, col: position.col },
      { row: position.row, col: position.col - 1 },
      { row: position.row, col: position.col + 1 },
    ];
    return adjacent.filter((pos) => this.isValidPosition(pos));
  }

  private getNearbySquares(position: Position, radius: number): Position[] {
    const nearby = [];
    for (let dr = -radius; dr <= radius; dr++) {
      for (let dc = -radius; dc <= radius; dc++) {
        if (dr === 0 && dc === 0) continue;
        const pos = { row: position.row + dr, col: position.col + dc };
        if (this.isValidPosition(pos)) {
          nearby.push(pos);
        }
      }
    }
    return nearby;
  }

  private calculateDistance(pos1: Position, pos2: Position): number {
    return Math.abs(pos1.row - pos2.row) + Math.abs(pos1.col - pos2.col);
  }

  private isValidPosition(position: Position): boolean {
    return (
      position.row >= 0 &&
      position.row < 8 &&
      position.col >= 0 &&
      position.col < 5
    );
  }

  private findOurKing(
    gameState: GameState
  ): { piece: IPiece; position: Position } | null {
    const ourColor = gameState.isLightTurn;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isKing && piece.isLight === ourColor) {
            return { piece, position: { row, col } };
          }
        }
      }
    }
    return null;
  }

  private findOpponentKing(
    gameState: GameState
  ): { piece: IPiece; position: Position } | null {
    const opponentColor = !gameState.isLightTurn;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isKing && piece.isLight === opponentColor) {
            return { piece, position: { row, col } };
          }
        }
      }
    }
    return null;
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }

  /**
   * Format a move for display in logs
   */
  private formatMove(gameState: GameState, moveInfo: MoveInfo): string {
    const from = moveInfo.fromPosition;
    const to = moveInfo.move.position;
    const piece = this.getPieceAt(gameState, from);
    const pieceSymbol = piece ? this.getPieceSymbol(piece) : "?";

    return `${pieceSymbol}[${from.row},${from.col}]→[${to.row},${to.col}]`;
  }

  /**
   * Get a symbol for the piece type
   */
  private getPieceSymbol(piece: IPiece): string {
    const symbols = {
      [PieceType.Bear]: piece.isLight ? "B" : "b",
      [PieceType.Crane]: piece.isLight ? "C" : "c",
      [PieceType.Frog]: piece.isLight ? "F" : "f",
      [PieceType.Fungi]: piece.isLight ? "U" : "u",
      [PieceType.Goat]: piece.isLight ? "G" : "g",
      [PieceType.Monkey]: piece.isLight ? "M" : "m",
      [PieceType.Octopus]: piece.isLight ? "O" : "o",
      [PieceType.Penguin]: piece.isLight ? "P" : "p",
      [PieceType.Rhino]: piece.isLight ? "R" : "r",
      [PieceType.Snake]: piece.isLight ? "S" : "s",
      [PieceType.Tree]: piece.isLight ? "T" : "t",
      [PieceType.Virus]: piece.isLight ? "V" : "v",
      [PieceType.Wolf]: piece.isLight ? "W" : "w",
    };

    return symbols[piece.type] || "?";
  }

  /**
   * Convert raw strategic scores to normalized priors (0.0 to 1.0)
   */
  private convertScoresToPriors(
    scoredMoves: Array<{ move: MoveInfo; score: number }>
  ): {
    positiveMovesCount: number;
    bestPrior: number;
    averagePrior: number;
  } {
    let positiveMovesCount = 0;
    let bestPrior = 0;
    let totalPriors = 0;

    // Find score range for normalization
    const scores = scoredMoves.map((sm) => sm.score);
    const minScore = Math.min(...scores);
    const maxScore = Math.max(...scores);
    const scoreRange = maxScore - minScore;

    for (const sm of scoredMoves) {
      const moveId = this.getMoveId(sm.move);

      // Normalize to 0.0-1.0 range
      let prior = 0.5; // Default neutral prior

      if (scoreRange > 0) {
        // Normalize score to 0.0-1.0 range
        const normalizedScore = (sm.score - minScore) / scoreRange;

        // Convert to prior: 0.1 minimum, 0.9 maximum
        prior = 0.1 + normalizedScore * 0.8;

        // Boost moves with positive strategic value
        if (sm.score > 0) {
          prior = Math.max(prior, 0.6); // Minimum 0.6 for positive moves
          positiveMovesCount++;
        }
      }

      this.strategicScores.set(moveId, prior);
      bestPrior = Math.max(bestPrior, prior);
      totalPriors += prior;
    }

    const averagePrior = totalPriors / scoredMoves.length;

    return {
      positiveMovesCount,
      bestPrior,
      averagePrior,
    };
  }

  /**
   * Generate move ID for scoring map
   */
  private getMoveId(moveInfo: MoveInfo): string {
    return `${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}->${moveInfo.move.position.row},${moveInfo.move.position.col}`;
  }
}

